import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API_URL from "../../config/ApiUrl";
import axios from "axios";
import { setAxiosInterceptor } from "./interceptorSlice";
import {
  setAxiosHeaders,
  setAxiosBaseUrl,
  // setAxiosInterceptor,
} from "./apiService";
import { CONSTANT } from "../../config/Constant";
import { getLocalStorageData, setLocalStorageData } from "../../config/Global";

export const initializeApp = createAsyncThunk(
  "auth/initializeApp",
  async (_, { dispatch, getState }) => {
    setAxiosBaseUrl();
    setAxiosInterceptor(dispatch, getState);
    dispatch(setHeaders());
  }
);

export const manageLocalStorage = createAsyncThunk(
  "auth/manageLocalStorage",
  async (_, { dispatch }) => {
    dispatch(setUserId(localStorage.getItem("userId")));
    dispatch(setBackOfficerId(localStorage.getItem("backOfficerId")));
  }
);

export const setHeaders = createAsyncThunk(
  "auth/setHeaders",
  async (access_token, { dispatch, getState }) => {
    setAxiosHeaders(access_token)
      .then((token) => {
        dispatch(setToken(token));
        dispatch(manageLocalStorage());

        dispatch(setModuleRightData(getLocalStorageData("moduleRights")));
        const { backOfficerId } = getState().auth;
        if (backOfficerId) {
          const payload = {
            entityId: backOfficerId,
            userType: CONSTANT.USER_TYPE.BACK_OFFICE,
          };
          dispatch(getUserProfileData(payload));
        }
      })
      .catch(() => {
        dispatch(setToken());
      });
  }
);

export const doLogin = createAsyncThunk(
  "auth/doLogin",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.VERIFICATION.LOGIN, payload);
      dispatch(setHeaders(data.token));
      dispatch(setRefreshToken(data.refreshToken));
      dispatch(getModuleRightsData(data.entityId));

      localStorage.setItem("userId", data.value);
      localStorage.setItem("backOfficerId", data.entityId);

      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Refresh Token API call
export const refreshTokenApi = createAsyncThunk(
  "auth/refreshToken",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      let { token } = getState().auth;
      const refreshToken = localStorage.getItem("refreshToken");
      const payload = {
        token: token,
        refreshToken: refreshToken,
      };
      const { data } = await axios.post(
        API_URL.VERIFICATION.REFRESH_TOKEN,
        payload
      );
      dispatch(setHeaders(data.token));
      setRefreshToken(data.refreshToken);
      dispatch(getModuleRightsData(data.entityId));
      return data.token;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getModuleRightsData = createAsyncThunk(
  "auth/getModuleRightsData",
  async (backOfficerId, { dispatch, rejectWithValue }) => {
    try {
      const data = await axios.get(
        `${API_URL.VERIFICATION.GET_MODULE_RIGHT}?entityId=${backOfficerId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendOTP = createAsyncThunk(
  "auth/sendOTP",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.VERIFICATION.SEND_OTP}`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async (payload, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams(payload);
      const queryString = params.toString();
      const { data } = await axios.post(
        `${API_URL.VERIFICATION.VERIFY_OTP}?${queryString}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// export const forgotPassword = createAsyncThunk(
//   "auth/forgotPassword",
//   async (payload, { rejectWithValue }) => {
//     try {
//       const { data } = await axios.post(
//         `${API_URL.VERIFICATION.FORGOT_PASSWORD}`,
//         payload
//       );
//       return data.data;
//     } catch (error) {
//       return rejectWithValue(error.data);
//     }
//   }
// );

export const doRegister = createAsyncThunk(
  "auth/doRegister",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.VERIFICATION.REGISTER}`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.VERIFICATION.CHANGE_PASSWORD}`,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getUserProfileData = createAsyncThunk(
  "auth/me",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const params = new URLSearchParams(payload);
      const queryString = params.toString();
      const data = await axios.get(
        `${API_URL.VERIFICATION.USER_PROFILE_DATA}?${queryString}`
      );
      dispatch(setUserProfileData(data.data));
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  token: null,
  isUnauthorise: false,
  userId: null,
  backOfficerId: null,
  error: null,
  isRefreshing: false,
  registerEmail: null,
  userProfileData: null,
  moduleRightsData: null,
  isRestricted: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setRefreshToken: (state, action) => {
      localStorage.setItem("refreshToken", action.payload);
    },
    setIsUnauthorise: (state, action) => {
      state.isUnauthorise = action.payload;
    },
    setIsRefreshingToken: (state, action) => {
      state.isRefreshing = action.payload;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setBackOfficerId: (state, action) => {
      state.backOfficerId = action.payload;
    },
    setRegisterEmail: (state, action) => {
      state.registerEmail = action.payload;
    },
    setUserProfileData: (state, action) => {
      state.userProfileData = action.payload;
      // localStorage.setItem("profileCompeleted", action.payload);
    },
    setModuleRightData: (state, action) => {
      state.moduleRightsData = action.payload;
    },
    setIsRestricted: (state, action) => {
      state.isRestricted = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(doLogin.fulfilled, (state, action) => {
        setHeaders(action.payload.token);
      })
      .addCase(doLogin.rejected, (state, action) => {
        state.token = null;
        state.error = action;
      })
      .addCase(getModuleRightsData.fulfilled, (state, action) => {
        const normalizedPermissions = {};
        action.payload.forEach(({ code, rightCode }) => {
          normalizedPermissions[code] = [...new Set(rightCode)];
        });
        state.moduleRightsData = normalizedPermissions;
        setLocalStorageData("moduleRights", normalizedPermissions);
      });
  },
});

export const {
  setToken,
  setRefreshToken,
  setIsUnauthorise,
  setIsRefreshingToken,
  setUserId,
  setBackOfficerId,
  setRegisterEmail,
  setUserProfileData,
  setModuleRightData,
  setIsRestricted,
} = authSlice.actions;
export default authSlice.reducer;
